import {
  useIsMobile,
  WSAlert,
  WSAvatar,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import {
  DataView,
  DataViewProps,
  DataViewSection
} from "../../../../shared/components/DataView";
import { selectorIsSharedFileNotNoneContext } from "../../selectors/selectorIsSharedFileNotNoneContext";
import { selectorIsSharedFileRequest } from "../../selectors/selectorIsSharedFileRequest";
import {
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";
import { LabelRequirementType } from "../LabelRequirementType";
import { LablePayeeName } from "../LablePayeeName";
import { LabelPayerName } from "../LablePayerName";

export type RequirementDetailsContentProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction?: RequirementTableCallback;
  customContext?: ContextItem;
  requirement: IRequirementResponse;
  showTitle?: boolean;
};

export const RequirementDetailsContent: React.FC<
  RequirementDetailsContentProps
> = ({
  requirement,
  contextType,
  isLoading,
  getRequirementName,
  getRequirementStatusAlert,
  getRequirementBasicInformation,
  getRequirementDetails,
  getRequirementDetailsWidgets,
  getRequirementDetailsProgressWidget,
  getRequirementDetailsHistoryWidget,
  getAdditionalData,
  getRequirementAdditionalContent,
  getTypeCellProps,
  onSuccessAction,
  showTitle = true
}) => {
  const isMobile = useIsMobile();
  const alertProps = getRequirementStatusAlert(requirement);
  const requirementBasicInformation =
    getRequirementBasicInformation(requirement) || [];
  const requirementDetails = getRequirementDetails(requirement);
  const requirementDetailsWidgets = getRequirementDetailsWidgets(requirement);
  const requirementName = getRequirementName(requirement);
  const progressWidget = getRequirementDetailsProgressWidget(requirement);
  const historyWidget = getRequirementDetailsHistoryWidget(requirement);
  const additionalData = getAdditionalData(requirement);
  const additionalDetailsContent = getRequirementAdditionalContent(requirement);

  return (
    <WSElement shimmer={isLoading}>
      <WSList gap="XL">
        <WSElement>
          <WSFlexBox.CenterY gap="L">
            {showTitle && <WSText.Heading2>{requirementName}</WSText.Heading2>}
            {contextType === RequirementContextType.Payee &&
            selectorIsSharedFileRequest(additionalData) &&
            selectorIsSharedFileNotNoneContext(additionalData.contextType) ? (
              <WSAvatar
                size="L"
                type="icon"
                icon="compass"
                format="square"
                colorBackground="garnet"
              />
            ) : null}
          </WSFlexBox.CenterY>

          <WSFlexBox mt="S" gap="L" alignItems="center">
            {contextType === RequirementContextType.Payee ? (
              <LablePayeeName payeeId={requirement.payeeId} />
            ) : contextType === RequirementContextType.Payer ? (
              <LabelPayerName payerId={requirement.payerId} />
            ) : null}

            <LabelRequirementType {...getTypeCellProps(requirement)} />
          </WSFlexBox>
        </WSElement>

        {alertProps && <WSAlert {...alertProps} />}
        <WSGrid>
          <WSGrid.Item span={{ l: "8" }}>
            <WSList gap="L">
              {isMobile ? progressWidget : null}
              <DataViewSection
                title="Requirement information"
                data={requirementBasicInformation}
                gridColumns={3}
              />
              {requirementDetails ? (
                React.isValidElement(requirementDetails) ? (
                  requirementDetails
                ) : (
                  <DataView {...(requirementDetails as DataViewProps)} />
                )
              ) : null}
              {additionalDetailsContent ? additionalDetailsContent : null}
            </WSList>
          </WSGrid.Item>
          <WSGrid.Item span={{ l: "4" }}>
            <WSList gap="L">
              {isMobile ? null : progressWidget}
              {requirementDetailsWidgets}
              {historyWidget}
            </WSList>
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
